import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios'; // Import Axios for API requests

import '../../css/sidebar/sidebar.css';
import ecom_color_logo from '../../img/ecom_logo_color.png';

// Brand Icons
import amazon_icon from '../../img/brandIcon/amazon_icon.png';
import flipkart_icon from '../../img/brandIcon/flipkart_icon.png';
import bigbasket_icon from '../../img/brandIcon/bigbasket_icon.png';
import blinkit_icon from '../../img/brandIcon/blinkit_icon.png';
import firstcry_icon from '../../img/brandIcon/firstcry_icon.png';
import instamart_icon from '../../img/brandIcon/instamart_icon.png';
import netmeds_icon from '../../img/brandIcon/netmeds_icon.png';
import purplle_icon from '../../img/brandIcon/purplle_icon.png';
import sanpdeal_icon from '../../img/brandIcon/sanpdeal_icon.png';
import tata1mg_icon from '../../img/brandIcon/tata1mg_icon.png';
import zepto_icon from '../../img/brandIcon/zepto_icon.png';
import tira_icon from '../../img/brandIcon/tira_icon.png';
import nykaa_icon from '../../img/brandIcon/nykaa_icon.png';
import amazonfesh_icon from '../../img/brandIcon/amazonfesh_icon.png';
import jiomart_icon from '../../img/brandIcon/jiomart_icon.png';

const Sidebar = ({ auth: { isAuthenticated, user } }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [subscribedPlatforms, setSubscribedPlatforms] = useState([]); // Store subscribed platforms
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscribedPlatforms();
    }
  }, [isAuthenticated]);

  // Define mapping from API response names to UI display names
  const platformMapping = {
    amazon: 'Amazon',
    flipkart: 'Flipkart',
    snapdeal: 'Snapdeal',
    first_cry: 'Firstcry',
    tata_1mg: 'TATA 1mg',
    netmeds: 'Netmeds',
    zepto: 'Zepto',
    instamart: 'Instamart',
    blinkit: 'Blinkit',
    bigbasket: 'BigBasket',
  };

  const platformIcons = {
    Amazon: amazon_icon,
    Flipkart: flipkart_icon,
    Snapdeal: sanpdeal_icon,
    Firstcry: firstcry_icon,
    'TATA 1mg': tata1mg_icon,
    Netmeds: netmeds_icon,
    Zepto: zepto_icon,
    Instamart: instamart_icon,
    Blinkit: blinkit_icon,
    BigBasket: bigbasket_icon,

    // Upcoming APIs
    Tira: tira_icon,
    Nykaa: nykaa_icon,
    Purplle: purplle_icon,
    'Jio Mart': jiomart_icon,
    'Amazon Fresh': amazonfesh_icon,
  };

  const fetchSubscribedPlatforms = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/ecomnext/api/user-subscribed-platform`
      );

      // Normalize API response (convert to lowercase) and map to expected names
      const apiPlatforms = response.data.platforms || [];

      // Convert API response values to match display names
      const normalizedPlatforms = apiPlatforms.map(
        (apiPlatform) =>
          platformMapping[apiPlatform.toLowerCase()] || apiPlatform
      );

      setSubscribedPlatforms(normalizedPlatforms);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const isDisabled = (platform) => {
    const apiOnDemandPlatforms = platforms['API on demand'];
    if (apiOnDemandPlatforms.includes(platform)) {
      return false; // Always enabled
    }
    return isAuthenticated && !subscribedPlatforms.includes(platform);
  };

  const isActive = (path) => location.pathname === path;

  const platforms = {
    'E-Commerce': [
      'Amazon',
      'Flipkart',
      'Snapdeal',
      'Firstcry',
      'TATA 1mg',
      'Netmeds',
    ],
    'Quick Commerce': ['Zepto', 'Instamart', 'Blinkit', 'BigBasket'],

    'API on demand': ['Tira', 'Nykaa', 'Purplle', 'Jio Mart', 'Amazon Fresh'],
  };

  return (
    <>
      <button
        className='fixed close-menu-button top-1 left-1 z-50 p-1 lg:hidden'
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? 'Close' : 'Menu'}
      </button>

      <div
        className={`sidebar fixed top-0 left-0 h-full w-64 transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform lg:translate-x-0 overflow-y-auto`}
      >
        <div className='flex flex-col items-center bg-[#FAF8FF]'>
          <div className='mb-12 flex justify-start w-full'>
            <Link to='/overview'>
              <img
                src={ecom_color_logo}
                alt='Logo'
                className='sidebar-logo mt-2 ml-3'
              />
            </Link>
          </div>

          <Link to='/overview' className='w-full sidebar-headings px-4'>
            <p
              className={`${
                isActive('/overview') ? 'active-section pt-1 pb-1 pl-2' : ''
              }`}
            >
              Overview
            </p>
          </Link>

          {Object.entries(platforms).map(([category, items]) => (
            <div key={category} className='w-full px-4 mt-7'>
              <h3 className='sidebar-headings mb-2'>{category}</h3>
              <hr className='heading-list-divider mb-3' />
              <ul className='space-y-2 sidebar-heading-lists'>
                {items.map((platform) => {
                  const path = `/${platform.toLowerCase().replace(/\s+/g, '')}`;
                  return (
                    <li
                      key={platform}
                      className={`${
                        isActive(path)
                          ? 'active-section pl-3 pt-0.5 pb-0.5'
                          : 'pl-3'
                      }`}
                    >
                      <Link
                        to={
                          category === 'API on demand'
                            ? '/contact-us'
                            : isDisabled(platform)
                            ? '#'
                            : path
                        }
                        className={`sidebar-heading-lists ${
                          isDisabled(platform)
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        <div className='flex items-center gap-2'>
                          {platformIcons[platform] && (
                            <img
                              src={platformIcons[platform]}
                              alt={`${platform} icon`}
                              className='w-4 h-4'
                            />
                          )}
                          <span>{platform}</span>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          <div className='w-full px-4 mt-7 mb-6'>
            <Link to='/help' className='sidebar-headings'>
              <p
                className={`${
                  isActive('/help') ? 'active-section pt-1 pb-1 pl-2' : ''
                }`}
              >
                Help
              </p>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {})(Sidebar);
